/**
 * Checkout Submit Handler
 *
 * Handles form submission for checkout:
 * - Sends analytics event to Google Analytics
 * - Updates commerce cart with billing and shipping information
 */

import { commerceCartOrderSetAllInformation } from '../../../../store/actions/Commerce';
import { DEFAULT_COUNTRY_CODE } from '../../../../config/constants';

import type {
  CheckoutFormValues,
  CheckoutSubmitOptions,
} from '../types/checkout.types';

/**
 * Formik form instance with submission control
 */
interface FormikForm {
  setSubmitting: (isSubmitting: boolean) => void;
}

/**
 * Handle checkout form submission
 *
 * Performs the following actions:
 * 1. Sends ecommerce tracking event to Google Analytics
 * 2. Dispatches action to update commerce order with billing/shipping info
 *
 * @param formValues - Validated checkout form values
 * @param form - Formik form instance
 * @param options - Additional options (pricing, analytics, redux actions)
 */
const checkoutSubmit = (
  formValues: CheckoutFormValues,
  form: FormikForm,
  {
    totalPrice,
    analyticsItems,
    dispatch,
    sendBillingInfo = true, // Default to true for backward compatibility
    sendShippingInfo = true, // Default to true for backward compatibility
  }: CheckoutSubmitOptions,
): void => {
  // Send analytics event
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'add_payment_info',
      ecommerce: {
        currency: 'EUR',
        value: totalPrice,
        items: analyticsItems,
      },
    });
  }

  const isBusiness = formValues.business_option === 'business';
  const billingAddressSame = formValues.delivery_differs_from_billing === false;

  const shippingOrganization = billingAddressSame
    ? formValues.billing_organization
    : formValues.organization;

  // Clean up address line 2 (remove spaces) - only when billing data is being sent
  if (sendBillingInfo) {
    // eslint-disable-next-line no-param-reassign
    formValues.billing_address_line2 = formValues.billing_address_line2.replace(
      / /g,
      '',
    );
  }
  // eslint-disable-next-line no-param-reassign
  formValues.address_line2 = formValues.address_line2.replace(/ /g, '');

  // Determine shipping address values
  // When sendShippingInfo is false, skip shipping (digital delivery)
  const getShippingValue = (
    billingValue: string,
    shippingValue: string,
  ): string => {
    if (!sendShippingInfo) return ''; // Free e-learning: no shipping needed
    return billingAddressSame ? billingValue : shippingValue;
  };

  // Dispatch action to update commerce order
  // When sendBillingInfo is false, both billing and shipping fields are empty (e-learning)
  dispatch(
    commerceCartOrderSetAllInformation(
      sendBillingInfo ? DEFAULT_COUNTRY_CODE : '',
      sendBillingInfo ? formValues?.billing_locality || '' : '',
      sendBillingInfo ? formValues?.billing_postal_code || '' : '',
      sendBillingInfo ? formValues?.billing_address_line1 || '' : '',
      sendBillingInfo ? formValues?.billing_address_line2 || '' : '',
      sendBillingInfo
        ? formValues?.billing_given_name || ''
        : formValues.personal_given_name,
      sendBillingInfo
        ? formValues?.billing_family_name || ''
        : formValues.personal_family_name,
      sendBillingInfo && isBusiness ? formValues.billing_organization : null,
      sendShippingInfo ? DEFAULT_COUNTRY_CODE : '',
      getShippingValue(formValues.billing_locality, formValues.locality),
      getShippingValue(formValues.billing_postal_code, formValues.postal_code),
      getShippingValue(
        formValues.billing_address_line1,
        formValues.address_line1,
      ),
      getShippingValue(
        formValues.billing_address_line2,
        formValues.address_line2,
      ),
      getShippingValue(formValues.billing_given_name, formValues.given_name),
      getShippingValue(formValues.billing_family_name, formValues.family_name),
      sendShippingInfo ? shippingOrganization || null : null,
      formValues?.email || '',
      // @ts-ignore
      sendBillingInfo ? formValues?.invoice_email || '' : '',
      sendBillingInfo ? formValues?.customer_reference || '' : '',
      formValues?.telephone || '',
      formValues?.order_someone_else || false,
    ),
  );

  // Stop form submission loading
  form.setSubmitting(false);
};

export default checkoutSubmit;
